import { useState } from "react";
import {
  AppShell as MantineAppShell,
  Burger,
  Group,
  NavLink,
  Text,
  ActionIcon,
  useMantineColorScheme,
  Tooltip,
  Divider,
  ScrollArea,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconDashboard,
  IconInbox,
  IconCheckbox,
  IconAlertTriangle,
  IconServer,
  IconUsers,
  IconSun,
  IconMoon,
  IconScan,
  IconHash,
  IconBroadcast,
  IconCloudUpload,
  IconBolt,
  IconBoltOff,
  IconActivity,
  IconSettings,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconLogout,
} from "@tabler/icons-react";
import { Link, useLocation } from "react-router-dom";
import RefreshControl from "./RefreshControl";
import { useAutoRefresh } from "../autoRefresh";
import { useAuth } from "../auth";

const NAV: Array<{ to: string; label: string; Icon: typeof IconDashboard; group?: string }> = [
  { to: "/", label: "Dashboard", Icon: IconDashboard },
  { to: "/payloads", label: "Payloads", Icon: IconInbox },
  { to: "/consumed", label: "Consumed", Icon: IconCheckbox },
  { to: "/exceptions", label: "Exceptions", Icon: IconAlertTriangle },
  { to: "/scan", label: "Scan", Icon: IconScan, group: "Scan pipeline" },
  { to: "/scan-pids", label: "Scan PIDs", Icon: IconHash },
  { to: "/scan-dispatch", label: "Scan Dispatch", Icon: IconBroadcast },
  { to: "/storage-relay", label: "Storage Relay", Icon: IconCloudUpload },
  { to: "/queues", label: "Queues", Icon: IconBolt, group: "Queues" },
  { to: "/queues-failed", label: "Queues failed", Icon: IconBoltOff },
  { to: "/servers", label: "Servers", Icon: IconServer, group: "Inventory" },
  { to: "/users", label: "Users", Icon: IconUsers },
  { to: "/recent-activity", label: "Recent Activity", Icon: IconActivity, group: "Audit" },
  { to: "/settings", label: "Settings", Icon: IconSettings, group: "Admin" },
];

export default function AppShell({ children }: { children: React.ReactNode }) {
  const [mobileOpened, { toggle: toggleMobile }] = useDisclosure();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    return localStorage.getItem("sidebarCollapsed") === "1";
  });
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const location = useLocation();
  const { forceRefresh } = useAutoRefresh();
  const { email, logout } = useAuth();

  const toggleCollapse = () => {
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem("sidebarCollapsed", next ? "1" : "0");
      return next;
    });
  };

  const navbarWidth = collapsed ? 64 : 240;

  return (
    <MantineAppShell
      header={{ height: 60 }}
      navbar={{ width: navbarWidth, breakpoint: "sm", collapsed: { mobile: !mobileOpened } }}
      padding="md"
    >
      <MantineAppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group wrap="nowrap" gap="sm">
            <Burger opened={mobileOpened} onClick={toggleMobile} hiddenFrom="sm" size="sm" />
            <Tooltip label={collapsed ? "Expand sidebar" : "Collapse sidebar"} withArrow>
              <ActionIcon
                visibleFrom="sm"
                variant="subtle"
                color="gray"
                onClick={toggleCollapse}
                aria-label="Toggle sidebar"
              >
                {collapsed ? <IconLayoutSidebarLeftExpand size={18} /> : <IconLayoutSidebarLeftCollapse size={18} />}
              </ActionIcon>
            </Tooltip>
            <img
              src="/favicon.png"
              alt="WPTailwatch logo"
              width={28}
              height={28}
              style={{ borderRadius: 6, objectFit: "contain" }}
            />
            <Text fw={800} size="lg" style={{ letterSpacing: "-0.01em" }}>
              CoreWatch
            </Text>
            <Tooltip label="Live operations — auto-refreshing data from the pipeline" withArrow>
              <span className="live-ops" aria-label="Live ops">
                <span className="live-ops-dot" aria-hidden="true" />
                <span className="live-ops-text">Live Ops</span>
              </span>
            </Tooltip>
          </Group>
          <Group wrap="nowrap" gap="lg">
            <RefreshControl />
            {email ? (
              <Text size="xs" c="dimmed" visibleFrom="md">
                {email}
              </Text>
            ) : null}
            <Tooltip label="Toggle theme" withArrow>
              <ActionIcon
                onClick={() => toggleColorScheme()}
                variant="default"
                aria-label="Toggle color scheme"
              >
                {colorScheme === "dark" ? <IconSun size={18} /> : <IconMoon size={18} />}
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Sign out" withArrow>
              <ActionIcon onClick={() => void logout()} variant="default" aria-label="Sign out">
                <IconLogout size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </MantineAppShell.Header>

      <MantineAppShell.Navbar>
        <ScrollArea h="100%" p={collapsed ? "xs" : "sm"} type="auto" scrollbarSize={6} offsetScrollbars>
          {NAV.map(({ to, label, Icon, group }, idx) => {
            const isActive = location.pathname === to;
            const link = (
              <NavLink
                component={Link}
                to={to}
                label={collapsed ? undefined : label}
                leftSection={<Icon size={18} />}
                active={isActive}
                onClick={() => {
                  if (mobileOpened) toggleMobile();
                  // Clicking the already-active link refreshes data on that
                  // page (same effect as the manual refresh icon).
                  if (isActive) forceRefresh();
                }}
                color="brand"
                style={collapsed ? { paddingInline: 6, justifyContent: "center" } : undefined}
              />
            );
            return (
              <div key={to}>
                {group && !collapsed ? (
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700} mt={idx === 0 ? 0 : "md"} mb={4} px={6}>
                    {group}
                  </Text>
                ) : null}
                {group && collapsed ? <Divider my="xs" /> : null}
                {collapsed ? (
                  <Tooltip label={label} position="right" withArrow>
                    {link}
                  </Tooltip>
                ) : (
                  link
                )}
              </div>
            );
          })}
          {!collapsed ? (
            <>
              <Divider my="md" />
              <Text size="xs" c="dimmed" px={6}>
                v0.2 · {email ?? "not signed in"}
              </Text>
            </>
          ) : null}
        </ScrollArea>
      </MantineAppShell.Navbar>

      <MantineAppShell.Main>{children}</MantineAppShell.Main>
    </MantineAppShell>
  );
}
